import { MonitorHeart } from "@mui/icons-material";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { Schedule } from "../../../../components/schedule";
import { useSessionContext } from "../../../../contexts/SessionContext";
import { GetConsents } from "../../../../services/consent";
import { GetAppointment } from "../../../../services/fhir";
import { DateFilterType } from "../../../../utils/interfaces";
import { useDatetime } from "../../../../utils/useDatetime";
import { fetchDependentAppointments } from "../../../../utils/patient/fetchDependentAppointments";
import { CarrouselMobile } from "../Caroussel/CarrouselMobile";
import { PatientQuestionnaireTasksSection } from "./components/PatientQuestionnaireTasksSection";
import QuickActionsMobile from "./compontents/QuickActionsMobile";
import { FitnessDataModal } from "./compontents/FitnessDataModal";

export function MobilePatient() {
  const { user, patientRelated } = useSessionContext();
  const [openFitnessModal, setOpenFitnessModal] = useState(false);
  const { formatDateToISO, isDateFilterTypeDay, isSameYearAndMonthAsToday } =
    useDatetime;

  const [received, setReceived] = useState<any>([]);
  const [appointments, setAppointments] = useState<any>([]);

  const [appointmentsDepedents, setAppointmentsDependents] = useState<any>([]);
  const [dependentes, setDependentes] = useState<any>([]);
  const [dateFilter, setDateFilter] = useState<Dayjs>(dayjs());
  const [filterType, setFilterType] = useState<DateFilterType>("month");
  const [loading, setLoading] = useState<boolean>(false);
  const [refetch, setRefetch] = useState<boolean>(false);

  useEffect(() => {
    async function fetchAppointments() {
      setLoading(true);

      // Calcula a data de início baseado no tipo de filtro
      const startDate =
        isDateFilterTypeDay(filterType) || isSameYearAndMonthAsToday(dateFilter)
          ? formatDateToISO(dateFilter)
          : filterType === "week"
            ? formatDateToISO(dateFilter.startOf("week"))
            : formatDateToISO(dateFilter.startOf("month"));

      // Calcula a data de fim baseado no tipo de filtro
      const endDate = isDateFilterTypeDay(filterType)
        ? formatDateToISO(dateFilter)
        : filterType === "week"
          ? formatDateToISO(dateFilter.endOf("week"))
          : formatDateToISO(dateFilter.endOf("month"));

      const response = await GetAppointment({
        ehrrunner: `${user.username}`,
        startDate,
        endDate,
        status: ["booked", "pending", "proposed"],
        sort: "date",
        includeLocation: true,
      });

      setAppointments(response);
      setLoading(false);
    }
    fetchAppointments();
  }, [
    dateFilter,
    filterType,
    user.username,
    formatDateToISO,
    isDateFilterTypeDay,
    isSameYearAndMonthAsToday,
    refetch,
  ]);

  useEffect(() => {
    async function fetchData() {
      try {
        const receivedResponse = await GetConsents({
          identifier: user.username,
          type: "received",
        });
        setReceived(
          receivedResponse?.data
            ?.filter(
              (el: any) =>
                el?.recurso?.id !== user.username && el?.recurso?.nome,
            )
            ?.map((el: any) => ({
              id: el?.recurso?.id,
              purpose: el?.contexto?.motivoAcesso,
            })),
        );
      } catch (err) {
        console.log("Fetch consents", err);
      } finally {
      }
    }
    fetchData();
  }, [user.username]);

  // Reabre o modal após o OAuth do Google Fit (mesmo fluxo do WebPatient).
  useEffect(() => {
    const shouldReopenModal =
      localStorage.getItem("reopenFitnessModalAfterLink") === "true";
    if (!shouldReopenModal) return;
    if (!user?.refreshTokenGoogleFit) return;

    setOpenFitnessModal(true);
    localStorage.removeItem("reopenFitnessModalAfterLink");
  }, [user?.refreshTokenGoogleFit]);

  useEffect(() => {
    const auxDependents = patientRelated?.filter((el: any) => el?.isVerify);
    if (auxDependents?.length) setDependentes(auxDependents);
    async function fetchAppointmentsDependentes() {
      setLoading(true);

      // Calcula a data de início baseado no tipo de filtro
      const startDate =
        isDateFilterTypeDay(filterType) || isSameYearAndMonthAsToday(dateFilter)
          ? formatDateToISO(dateFilter)
          : filterType === "week"
            ? formatDateToISO(dateFilter.startOf("week"))
            : formatDateToISO(dateFilter.startOf("month"));

      // Calcula a data de fim baseado no tipo de filtro
      const endDate = isDateFilterTypeDay(filterType)
        ? formatDateToISO(dateFilter)
        : filterType === "week"
          ? formatDateToISO(dateFilter.endOf("week"))
          : formatDateToISO(dateFilter.endOf("month"));

      // Centraliza consentimento + fetch de appointments dos dependentes.
      const allAppointments = await fetchDependentAppointments({
        dependents: auxDependents,
        receivedConsents: received,
        startDate,
        endDate,
      });

      setAppointmentsDependents(allAppointments);
      setLoading(false);
    }

    if (auxDependents?.length && received?.length)
      fetchAppointmentsDependentes();
  }, [
    dateFilter,
    filterType,
    user.username,
    formatDateToISO,
    isDateFilterTypeDay,
    isSameYearAndMonthAsToday,
    patientRelated,
    received,
    refetch,
  ]);

  return (
    <>
      <Stack direction="column" spacing={2}>
        <Stack spacing="20px">
          <QuickActionsMobile />
          <PatientQuestionnaireTasksSection variant="tutor" />
        </Stack>
        {/* <JourneyCards /> */}
        <Box
          sx={{
            width: { xs: "100%", lg: "70%" },
            //height: "100%",
            backgroundColor: "neutral0.main",
            borderRadius: { xs: "4px", md: "16px" },
            mb: 2,
            padding: { xs: "16px", md: "24px 40px" },
            boxShadow:
              "rgba(0, 0, 0, 0.2) 0px 3px 1px -2px, rgba(0, 0, 0, 0.14) 0px 2px 2px 0px, rgba(0, 0, 0, 0.12) 0px 1px 5px 0px",
          }}
        >
          <Schedule
            relatedPatient={dependentes}
            bundle={{
              ...appointments,
              entry: [
                ...(appointments?.entry || []), // Verifica se appointments.entry é um array válido, senão usa um array vazio
                ...(appointmentsDepedents || []), // Verifica se appointmentsDepedents é um array válido, senão usa um array vazio
              ],
            }}
            dateFilter={dateFilter}
            filterType={filterType}
            setDateFilter={setDateFilter}
            setFilterType={setFilterType}
            loading={loading}
            setRefetch={setRefetch}
            refetch={refetch}
          />
        </Box>

        {/* Acesso aos dados fitness (Google Fit), alinhado ao atalho da versão web. */}
        {/* <Box
          sx={{
            width: { xs: "100%", lg: "70%" },
            backgroundColor: "neutral0.main",
            borderRadius: { xs: "4px", md: "16px" },
            mb: 2,
            boxShadow:
              "rgba(0, 0, 0, 0.2) 0px 3px 1px -2px, rgba(0, 0, 0, 0.14) 0px 2px 2px 0px, rgba(0, 0, 0, 0.12) 0px 1px 5px 0px",
            overflow: "hidden",
          }}
        >
          <Card elevation={0} sx={{ borderRadius: 0, bgcolor: "transparent" }}>
            <CardActionArea onClick={() => setOpenFitnessModal(true)}>
              <CardContent sx={{ py: 2, px: { xs: 2, md: 3 } }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <MonitorHeart sx={{ fontSize: 40, color: "primary800.main" }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} color="primary800.main">
                      Dados fitness
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Google Fit — passos, sono e outras métricas
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        </Box> */}

        {/* <CarrouselMobile /> */}
      </Stack>
      <FitnessDataModal
        open={openFitnessModal}
        onClose={() => setOpenFitnessModal(false)}
        refreshToken={user?.refreshTokenGoogleFit || ""}
      />
    </>
  );
}
